import React from 'react';
import PropTypes from 'prop-types';
import { toJS, observable, isObservableArray } from 'mobx';
import { observer } from 'mobx-react';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import debounce from 'lodash/debounce';

import Select from './Select';
import Pager from '../pager';

const PER_PAGE = 9;

const mobxArrayCustomiser = (first, other) => {
	let reInvoke = false;
	if (isObservableArray(first) || isObservableArray(other)) {
		first = first.peek();
		reInvoke = true;
	}
	if (isObservableArray(other)) {
		other = other.peek();
		reInvoke = true;
	}
	if (reInvoke) {
		return isEqualWith(first,other,mobxArrayCustomiser);
	}
};

@observer
export default class RecordSelect extends React.Component {

	static propTypes = {
		model: PropTypes.func.isRequired,
		value: PropTypes.any,
		onChange: PropTypes.func,
		property: PropTypes.string,
		searchFields: PropTypes.array,
		computed: PropTypes.func,
		filter: PropTypes.object,
		empty: PropTypes.any,
		comparator: PropTypes.func,
		onSearch: PropTypes.func,
		noSearch: PropTypes.bool,
		noTotal: PropTypes.bool,
		className: PropTypes.string,
		disabled: PropTypes.bool,
		isRequired: PropTypes.bool,
		position: PropTypes.string,
		prependItems: PropTypes.array,
		autoActivate: PropTypes.bool,
	};

	static defaultProps = {
		empty: '-',
		multiple: false,
		isRequired: false,
		autoActivate: false,
		prependItems: [],
		filter: {},
		__data: {},
	};

	@observable records = [];
	@observable value = null;
	@observable isLoading = false;
	@observable search = '';
	@observable page = 1;

	constructor(props) {
		super(props);
		this.store = props.store;
		// console.log('relationData', relationData);
		this.reload = debounce(this.reload, 250, { leading: false, trailing: true });
	}

	componentDidMount() {
		this.reload();
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		const prev = toJS(prevProps.filter);
		const next = toJS(this.props.filter);
		// const equal = isEqual(prev, next);
		const equal = isEqualWith(prev, next, mobxArrayCustomiser);
		// console.log('prev', JSON.stringify(prev));
		// console.log('next', JSON.stringify(next));
		// console.log('equal', equal);
		if (!equal) {
			this.page = 1;
			this.search = '';
			this.reload();
		}
	}

	reload = async () => {
		this.isLoading = true;

		const trimmed = this.search.trim();
		const filter = { ...this.props.filter };
		let search = {};

		if (trimmed.length > 0) {
			if (this.props.searchFields && this.props.searchFields.length > 0) {
				// const fields = this.props.searchFields.map(field => ({ [field]: { ilike: `%${trimmed}%` }}));
				const fields = this.props.searchFields.map(field => {
					if (typeof field === 'string') {
						return { [field]: { ilike: `%${trimmed}%` } };
					}
					else if (typeof field === 'object') {
						const { name, start } = field;
						return { [name]: { ilike: start ? `${trimmed}%` : `%${trimmed}%` } };
					}
				});
				const where = fields.length > 1 ? { or: fields }: fields[0];
				if (filter.and) {
					filter.and.push(where);
				}
				else if (!filter.where) {
					filter.where = where;
				}
				else {
					filter.where = { and: [ filter.where, where ]};
				}
			}
			else {
				search = { search: trimmed };
			}
		}

		// console.log('filter', filter, trimmed);

		this.records = await this.props.model.find({
			order: `${this.props.property || 'id'} asc`,
			...filter,
			...search,
			skip: (this.page - 1) * (PER_PAGE - this.props.prependItems.length),
			limit: PER_PAGE - this.props.prependItems.length,
		});

		this.isLoading = false;
	};

	onMount = el => this.el = el;

	isObjectItem = item => item && typeof item === 'object' && item.hasOwnProperty('label') && item.hasOwnProperty('value');

	itemValue = item => this.isObjectItem(item) ? item.value : item;
	itemLabel = item => this.isObjectItem(item) ? item.label : item;

	comparator = (a, b) => {
		return this.props.comparator ?
			this.props.comparator(this.itemValue(a), this.itemValue(b))
			:
			a && b && this.itemValue(a).id === this.itemValue(b).id;
	};

	onSearch = async str => {
		this.search = str;
		this.page = 1;
		if (this.props.onSearch) {
			this.props.onSearch(str);
		}
		else {
			setTimeout(() => this.reload(), 0);
		}
	};

	onPageChange = page => {
		this.page = page;
		this.reload();
	};

	onSelectItem = async selectedItem => {
		const itemValue = this.itemValue(selectedItem);
		if (this.props.onChange) {
			await this.props.onChange(itemValue);
		}
	};

	render() {
		let {
			model,
			value,
			onChange,
			computed,
			selected,
			showValue,
			property,
			empty,
			noTotal,
			className,
			prependItems,
			...rest
		} = this.props;

		// console.log('value', value && value.id);

		const valueItem = {
			label: value ? (computed ? computed(value) : value[property]) : empty,
			value,
		};

		const _showValue = showValue || this.itemLabel(valueItem);
		const _selected = value ? valueItem : null;

		let _items = this.records.map(record => ({
			label: computed ? computed(record) : record[property],
			value: record,
		}));

		const footer = <div className="actions">
			<Pager
				totalCount={this.records.totalCount}
				itemsPerPage={PER_PAGE - this.props.prependItems.length}
				current={this.page}
				onChange={this.onPageChange}
			/>
		</div>;

		return <>
			<Select
				value={valueItem}
				items={[...prependItems, ..._items]}
				selected={_selected}
				className={'record-select ' + (className || '')}
				listClassName="record-select-list"
				showValue={_showValue || empty}
				emptyContent={null}
				getRef={this.onMount}
				onChange={this.onSelectItem}
				comparator={this.comparator}
				onSearch={this.onSearch}
				isLoading={this.isLoading}
				footer={noTotal ? null : footer}
				empty={empty}

				{...rest}
				// isRequired={this.props.isRequired}
				// empty={this.props.empty}
				// noSearch={this.props.noSearch}
				// disabled={this.props.disabled}
				// position={this.props.position}
				// alwaysActive={this.props.alwaysActive}
				// autoActivate={this.props.autoActivate}
			/>
		</>;
	}

}
